import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { formatMoneyFromCents, updateAtcPreorderLabel } from '@/scripts/product/update-atc-status-label'

type CharmSelection = {
	index: number
	id: string
	title: string
	price: number
	imageUrl: string
	quantity: number
}

type ProductFormBeforeSubmitEvent = CustomEvent<{
	formData: FormData
}>

type ActiveCharmDiscount = {
	amount: number
	freeCount: number
	title: string
	source: 'discount-kit-live' | 'theme-setting' | 'none'
	rewardType: string
	thresholds: number[]
	rewardValues: number[]
}

const PURCHASEABLE_STATUSES = new Set(['InStock', 'PreOrder'])

export class CharmsBuilder extends UcoastEl {
	static htmlSelector = 'charms-builder'

	private section: HTMLElement | null
	private externalProductForm: HTMLElement | null
	private externalForm: HTMLFormElement | null
	private submitButton: HTMLButtonElement | null
	private currentVariantInput: HTMLInputElement | null
	private optionButtons: NodeListOf<HTMLButtonElement>
	private summaryList?: HTMLElement
	private summary?: HTMLElement
	private totalEl?: HTMLElement
	private originalTotalEl?: HTMLElement
	private countCopyEl?: HTMLElement
	private lineCopyEl?: HTMLElement
	private linePriceEl?: HTMLElement
	private discountRow?: HTMLElement
	private discountCopyEl?: HTMLElement
	private discountPriceEl?: HTMLElement
	private discountMessageEl?: HTMLElement
	private basePriceEl?: HTMLElement
	private baseCopyEl?: HTMLElement
	private debugEl?: HTMLElement
	private debugOutputEl?: HTMLElement
	private tileRemoveButtons: NodeListOf<HTMLButtonElement>
	private charmsByIndex = new Map<number, CharmSelection>()
	private previousQuantities = new Map<number, number>()

	constructor() {
		super()

		this.section = this.closest('section[id^="MainProduct-"]')
		this.externalProductForm = this.section?.querySelector<HTMLElement>('product-form:not(.charms-builder__product-form)') ?? null
		this.externalForm = this.externalProductForm?.querySelector<HTMLFormElement>('form') ?? null
		this.submitButton = this.externalProductForm?.querySelector<HTMLButtonElement>('.product-form__submit') ?? null
		this.currentVariantInput =
			(this.externalForm?.querySelector<HTMLInputElement>('[data-uc-product-variant-id], [name="id"], [name="items[0][id]"]') as HTMLInputElement | null) ??
			null
		this.optionButtons = q.rl<HTMLButtonElement>('[data-uc-charms-option]', this)
		this.summaryList = q.os<HTMLElement>('[data-uc-charms-summary-list]', this)
		this.summary = q.os<HTMLElement>('[data-uc-charms-summary]', this)
		this.totalEl = q.os<HTMLElement>('[data-uc-charms-total]', this)
		this.originalTotalEl = q.os<HTMLElement>('[data-uc-charms-original-total]', this)
		this.countCopyEl = q.os<HTMLElement>('[data-uc-charms-count-copy]', this)
		this.lineCopyEl = q.os<HTMLElement>('[data-uc-charms-line-copy]', this)
		this.linePriceEl = q.os<HTMLElement>('[data-uc-charms-line-price]', this)
		this.discountRow = q.os<HTMLElement>('[data-uc-charms-discount-row]', this)
		this.discountCopyEl = q.os<HTMLElement>('[data-uc-charms-discount-copy]', this)
		this.discountPriceEl = q.os<HTMLElement>('[data-uc-charms-discount-price]', this)
		this.discountMessageEl = q.os<HTMLElement>('[data-uc-charms-discount-message]', this)
		this.basePriceEl = q.os<HTMLElement>('[data-uc-charms-base-price]', this)
		this.baseCopyEl = q.os<HTMLElement>('[data-uc-charms-base-copy]', this)
		this.debugEl = q.os<HTMLElement>('[data-uc-charms-debug]', this)
		this.debugOutputEl = q.os<HTMLElement>('[data-uc-charms-debug-output]', this)
		this.tileRemoveButtons = q.rl<HTMLButtonElement>('[data-uc-charms-tile-remove]', this)

		for (const button of this.optionButtons) {
			button.addEventListener('click', this.onOptionClick.bind(this))
		}
		for (const button of this.tileRemoveButtons) {
			button.addEventListener('click', this.onTileRemoveClick.bind(this))
		}

		this.summaryList?.addEventListener('click', this.onSummaryClick.bind(this))
		this.externalForm?.addEventListener(
			'submit',
			this.onExternalSubmitCapture.bind(this),
			{ capture: true }
		)
		this.externalProductForm?.addEventListener(
			'uc:product-form:before-submit',
			this.onProductFormBeforeSubmit.bind(this) as EventListener
		)
		this.section
			?.querySelectorAll<HTMLInputElement>('.size-radios__size-input')
			.forEach((input) => input.addEventListener('change', this.syncUI.bind(this)))

		this.syncUI()
	}

	private onOptionClick(event: Event) {
		const button = q.rct<Event, HTMLButtonElement>(event)
		const optionIndex = Number(button.dataset.charmIndex)
		if (!Number.isFinite(optionIndex)) return

		const existing = this.charmsByIndex.get(optionIndex)
		const nextQuantity = (existing?.quantity || 0) + 1

		this.charmsByIndex.set(optionIndex, {
			index: optionIndex,
			id: button.dataset.charmId || '',
			title: button.dataset.charmTitle || '',
			price: Number(button.dataset.charmPrice || 0),
			imageUrl: button.dataset.charmImageUrl || '',
			quantity: nextQuantity,
		})

		this.syncUI()
	}

	private onTileRemoveClick(event: Event) {
		event.preventDefault()
		const button = q.rct<Event, HTMLButtonElement>(event)
		const optionIndex = Number(button.dataset.charmIndex)
		if (!Number.isFinite(optionIndex)) return

		this.decrementSelection(optionIndex)
		this.syncUI()
	}

	private onSummaryClick(event: Event) {
		const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-uc-charms-remove]')
		if (!button) return

		event.preventDefault()
		const optionIndex = Number(button.dataset.charmIndex)
		if (!Number.isFinite(optionIndex)) return

		this.decrementSelection(optionIndex)
		this.syncUI()
	}

	private onExternalSubmitCapture() {
		this.syncUI()
	}

	private onProductFormBeforeSubmit(event: ProductFormBeforeSubmitEvent) {
		const formData = event.detail?.formData
		if (!formData) return
		const totalSelectedCount = this.getTotalSelectedCount()
		const hideCartLineItemDetails =
			this.dataset.showCartLineItemDetails !== 'true' ||
			this.closest('.product--charm-slipper') !== null

		const baseVariantId = String(
			formData.get('items[0][id]') || formData.get('id') || this.currentVariantInput?.value || ''
		)
		if (!baseVariantId) return

		const buildId = `charms-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
		const existingItemIndexes = Array.from(formData.keys())
			.map((key) => key.match(/^items\[(\d+)\]\[id\]$/))
			.filter((match): match is RegExpMatchArray => match !== null)
			.map((match) => Number(match[1]))
			.filter((value) => Number.isFinite(value))
		const hasItemPayload = existingItemIndexes.length > 0

		if (!hasItemPayload) {
			formData.set('items[0][id]', baseVariantId)
			formData.delete('id')
		}

		const sellingPlan = formData.get('selling_plan')
		if (sellingPlan && !formData.get('items[0][selling_plan]')) {
			formData.set('items[0][selling_plan]', String(sellingPlan))
			formData.delete('selling_plan')
		}

		formData.set('items[0][properties[_charms_build_id]]', buildId)
		formData.delete('items[0][properties[PREORDER]]')
		formData.delete('items[0][properties[_preorder]]')
		formData.delete('items[0][properties[_stoq_shipping_text]]')
		if (hideCartLineItemDetails) {
			formData.set('items[0][properties[_hide_cart_line_item_props]]', 'true')
		} else {
			formData.delete('items[0][properties[_hide_cart_line_item_props]]')
		}
		if (totalSelectedCount > 0) {
			formData.set('items[0][properties[_has_customization]]', 'true')
		}

		if (totalSelectedCount === 0) return

		let nextIndex =
			existingItemIndexes.length > 0
				? Math.max(...existingItemIndexes) + 1
				: 1

		for (const selection of this.getSelections()) {
			formData.set(`items[${nextIndex}][id]`, selection.id)
			formData.set(`items[${nextIndex}][quantity]`, String(selection.quantity))
			formData.set(`items[${nextIndex}][parent_id]`, baseVariantId)
			formData.set(`items[${nextIndex}][properties[_parent_id]]`, baseVariantId)
			formData.set(`items[${nextIndex}][properties[_parent_build_id]]`, buildId)
			formData.delete(`items[${nextIndex}][properties[PREORDER]]`)
			formData.delete(`items[${nextIndex}][properties[_preorder]]`)
			formData.delete(`items[${nextIndex}][properties[_stoq_shipping_text]]`)
			if (hideCartLineItemDetails) {
				formData.set(`items[${nextIndex}][properties[_hide_cart_line_item_props]]`, 'true')
			} else {
				formData.delete(`items[${nextIndex}][properties[_hide_cart_line_item_props]]`)
			}
			nextIndex += 1
		}
	}

	private syncUI() {
		for (const button of this.optionButtons) {
			const optionIndex = Number(button.dataset.charmIndex)
			const selection = this.charmsByIndex.get(optionIndex)
			const tile = button.closest<HTMLElement>('[data-uc-charms-tile]')
			const countBadge = q.os<HTMLElement>('[data-uc-charms-count-badge]', button)
			const removeButton = tile
				? q.os<HTMLButtonElement>('[data-uc-charms-tile-remove]', tile)
				: undefined
			const quantity = selection?.quantity || 0
			const previousQuantity = this.previousQuantities.get(optionIndex) || 0

			tile?.classList.toggle('active', quantity > 0)
			if (countBadge) {
				countBadge.classList.toggle('hidden', quantity < 1)
				countBadge.textContent = quantity > 0 ? String(quantity) : ''
			}
			if (removeButton) {
				removeButton.classList.toggle('hidden', quantity < 1)
			}
			if (tile && previousQuantity !== quantity) {
				this.animateTile(tile, quantity > previousQuantity ? 'is-adding' : 'is-removing')
			}
			this.previousQuantities.set(optionIndex, quantity)
		}

		this.renderSummary()
		this.syncExternalSubmitButton()
	}

	private renderSummary() {
		if (!this.summary || !this.summaryList) return

		const selections = this.getSelections()
		const totalSelectedCount = this.getTotalSelectedCount()
		const basePrice = this.getBasePrice()
		const charmSubtotal = selections.reduce(
			(total, selection) => total + selection.price * selection.quantity,
			0
		)
		const activeDiscount = this.getActiveDiscount(totalSelectedCount, charmSubtotal)
		const { amount: discount, freeCount, title: discountTitle } = activeDiscount
		const total = basePrice + charmSubtotal - discount
		const hasSelections = totalSelectedCount > 0

		this.summary.toggleAttribute('hidden', !hasSelections)
		this.summaryList.toggleAttribute('hidden', selections.length === 0)
		this.summaryList.dataset.itemCount = String(selections.length)

		if (this.baseCopyEl) this.baseCopyEl.textContent = this.getBaseSummaryLabel()
		if (this.basePriceEl) this.basePriceEl.textContent = formatMoneyFromCents(basePrice)
		if (this.totalEl) this.totalEl.textContent = formatMoneyFromCents(total)
		if (this.linePriceEl) this.linePriceEl.textContent = formatMoneyFromCents(charmSubtotal)
		if (this.lineCopyEl) {
			const charmsLineLabel = this.template(
				this.dataset.charmsLineLabel || '{{ count }} charms',
				totalSelectedCount
			)
			const unitPriceCents = selections[0]?.price || Number(this.dataset.charmsUnitPriceCents || 0)
			if (unitPriceCents > 0) {
				const unitLabel = this.template(
					this.dataset.charmsLinePriceLabel || '{{ price }} each',
					totalSelectedCount,
					formatMoneyFromCents(unitPriceCents)
				)
				this.lineCopyEl.textContent = `${charmsLineLabel} · ${unitLabel}`
			} else {
				this.lineCopyEl.textContent = charmsLineLabel
			}
		}
		if (this.countCopyEl) {
			const countCopy = this.template(
				this.dataset.charmsLineLabel || '{{ count }} charms',
				totalSelectedCount
			)
			if (freeCount > 0) {
				const freeCountText = this.template(
					this.dataset.freeCountLabel || '{{ count }} free',
					freeCount
				)
				this.countCopyEl.textContent = `${countCopy} · ${freeCountText}`
			} else {
				this.countCopyEl.textContent = countCopy
			}
			this.setElementVisible(this.countCopyEl, hasSelections)
		}

		if (this.discountRow && this.discountCopyEl && this.discountPriceEl && this.discountMessageEl && this.originalTotalEl) {
			const hasDiscount = discount > 0
			this.setElementVisible(this.discountRow, hasDiscount)
			this.setElementVisible(this.discountMessageEl, hasDiscount)
			this.setElementVisible(this.originalTotalEl, hasDiscount)

			if (hasDiscount) {
				this.discountCopyEl.textContent =
					freeCount > 0
						? this.template(
							this.dataset.discountLabel || '{{ count }} free',
							freeCount
						)
						: discountTitle || 'Discount'
				this.discountPriceEl.textContent = `-${formatMoneyFromCents(discount)}`
				this.discountMessageEl.textContent =
					freeCount > 0
						? this.template(
							this.dataset.discountMessage || '{{ count }} free charm applied.',
							freeCount
						)
						: discountTitle
				this.originalTotalEl.textContent = formatMoneyFromCents(basePrice + charmSubtotal)
			}
		}

		if (selections.length === 0) {
			this.summaryList.innerHTML = ''
			delete this.summaryList.dataset.itemCount
			return
		}

		const summaryHtml = selections
			.map((selection) => {
				const escapedTitle = this.escapeHtml(selection.title)
				const previewImage = selection.imageUrl
					? `<img class="charms-builder__summary-image" src="${selection.imageUrl}" alt="${escapedTitle}">`
					: ''
				const removeLabelTemplate =
					this.dataset.removeLabelTemplate || 'Remove [title]'
				const removeLabel = this.escapeHtml(
					removeLabelTemplate.replace('[title]', selection.title)
				)
				return `
					<div class="charms-builder__summary-item">
						<button type="button" class="charms-builder__summary-remove semantic-button" data-uc-charms-remove data-charm-index="${selection.index}" aria-label="${removeLabel}">
							&times;
						</button>
						<span class="charms-builder__summary-count">${selection.quantity}</span>
						<div class="charms-builder__summary-media">${previewImage}</div>
					</div>
				`
			})
			.join('')

		this.summaryList.innerHTML = summaryHtml
		this.renderDebug({
			activeDiscount,
			basePrice,
			charmSubtotal,
			total,
			totalSelectedCount,
		})
	}

	private syncExternalSubmitButton() {
		if (!this.submitButton) return

		const totalSelectedCount = this.getTotalSelectedCount()
		const inventoryStatus = this.getInventoryStatus()
		if (!PURCHASEABLE_STATUSES.has(inventoryStatus)) return

		const activeLabelTarget = this.getActiveSubmitLabelTarget()
		if (!activeLabelTarget) return

		if (totalSelectedCount < 1) {
			this.captureBaseSubmitState(activeLabelTarget)
			this.submitButton.dataset.ucCharmsOverrideActive = 'false'
			updateAtcPreorderLabel(
				activeLabelTarget,
				this.submitButton.dataset.ucCharmsBaseLabel?.trim() || this.getBaseSubmitLabel(),
				this.getBasePrice()
			)
			return
		}

		if (this.submitButton.dataset.ucCharmsOverrideActive !== 'true') {
			this.captureBaseSubmitState(activeLabelTarget)
		}

		this.submitButton.dataset.ucCharmsOverrideActive = 'true'
		const price = this.getTotalPrice()
		const label =
			this.submitButton.dataset.ucCharmsBaseLabel?.trim() ||
			this.dataset.selectedButtonLabel ||
			'Add to cart'
		updateAtcPreorderLabel(activeLabelTarget, label, price)
	}

	private getSelections() {
		return Array.from(this.charmsByIndex.entries())
			.sort((a, b) => a[0] - b[0])
			.map(([, selection]) => selection)
	}

	private getActiveDiscount(totalSelectedCount: number, charmSubtotal: number): ActiveCharmDiscount {
		const liveDiscount = this.getDiscountKitDiscount(totalSelectedCount, charmSubtotal)
		if (liveDiscount) return liveDiscount

		const freeCount = this.getFreeCharmCount(totalSelectedCount)
		return {
			amount: this.getDiscountAmount(freeCount),
			freeCount,
			title: '',
			source: freeCount > 0 ? 'theme-setting' : 'none',
			rewardType: freeCount > 0 ? 'FREE_CHEAPEST_UNITS' : '',
			thresholds: freeCount > 0 ? [Number(this.dataset.freeCharmEveryCount || 0)] : [],
			rewardValues: freeCount > 0 ? [1] : [],
		}
	}

	private getDiscountKitDiscount(totalSelectedCount: number, charmSubtotal: number): ActiveCharmDiscount | null {
		const rawDiscount = this.parseJsonData(this.dataset.discountKitRaw)
		const customDiscount = this.getCustomDiscountKitDiscount(
			this.isRecord(rawDiscount) ? rawDiscount : null,
			totalSelectedCount,
			charmSubtotal
		)
		if (customDiscount) return customDiscount

		const rewardType = this.dataset.discountKitRewardType?.trim()
		const thresholds = this.parseNumberList(this.dataset.discountKitThresholds)
		const rewardValues = this.parseNumberList(this.dataset.discountKitRewardValues)
		if (!rewardType || thresholds.length === 0 || rewardValues.length === 0) return null

		let matchedTierIndex = -1
		for (const [index, threshold] of thresholds.entries()) {
			if (totalSelectedCount >= threshold) {
				matchedTierIndex = index
			}
		}
		if (matchedTierIndex < 0) return null

		const rewardValue = rewardValues[Math.min(matchedTierIndex, rewardValues.length - 1)]
		if (!Number.isFinite(rewardValue) || rewardValue <= 0) return null

		let discountAmount = 0
		if (rewardType === 'PERCENTAGE') {
			discountAmount = Math.round(charmSubtotal * (rewardValue / 100))
		} else if (rewardType === 'FIXED_AMOUNT') {
			discountAmount = rewardValue
		} else if (rewardType === 'FIXED_PRICE') {
			discountAmount = Math.max(0, charmSubtotal - rewardValue * totalSelectedCount)
		}

		if (!Number.isFinite(discountAmount) || discountAmount <= 0) return null

		return {
			amount: Math.min(charmSubtotal, discountAmount),
			freeCount: this.getFreeCountFromDiscountAmount(discountAmount),
			title: this.dataset.discountKitTitle?.trim() || '',
			source: 'discount-kit-live',
			rewardType,
			thresholds,
			rewardValues,
		}
	}

	private getCustomDiscountKitDiscount(
		rawDiscount: Record<string, unknown> | null,
		totalSelectedCount: number,
		charmSubtotal: number
	): ActiveCharmDiscount | null {
		if (!rawDiscount || rawDiscount.discount_type !== 'CUSTOM') return null

		const config =
			typeof rawDiscount.config === 'object' && rawDiscount.config !== null
				? (rawDiscount.config as Record<string, unknown>)
				: null
		const rules = Array.isArray(config?.discountRules) ? config.discountRules : []
		if (rules.length < 1) return null

		const matchingRule = rules.find((rule): rule is Record<string, unknown> => {
			if (typeof rule !== 'object' || rule === null) return false
			return Array.isArray(rule.prereqParts) && Array.isArray(rule.targetParts)
		})
		if (!matchingRule) return null

		const prereqParts = matchingRule.prereqParts as Array<Record<string, unknown>>
		const targetParts = matchingRule.targetParts as Array<Record<string, unknown>>
		const recurring = matchingRule.recurring !== false
		const maxRecurrences = Number(matchingRule.maxRecurrences)
		const discountValue =
			typeof matchingRule.discountValue === 'object' && matchingRule.discountValue !== null
				? (matchingRule.discountValue as Record<string, unknown>)
				: null
		const percentageValue = Number(
			typeof discountValue?.percentage === 'object' && discountValue.percentage !== null
				? (discountValue.percentage as Record<string, unknown>).value
				: NaN
		)

		const prereqQuantity = prereqParts.reduce((total, part) => {
			return total + Math.max(0, Number(part.quantity) || 0)
		}, 0)
		const targetQuantity = targetParts.reduce((total, part) => {
			return total + Math.max(0, Number(part.quantity) || 0)
		}, 0)
		const itemsPerRecurrence = prereqQuantity + targetQuantity
		if (itemsPerRecurrence <= 0 || targetQuantity <= 0) return null

		let recurrences = 0
		if (recurring) {
			recurrences = Math.floor(totalSelectedCount / itemsPerRecurrence)
		} else if (totalSelectedCount >= itemsPerRecurrence) {
			recurrences = 1
		}
		if (Number.isFinite(maxRecurrences) && maxRecurrences > 0) {
			recurrences = Math.min(recurrences, maxRecurrences)
		}
		if (recurrences <= 0) return null

		const discountedUnits = recurrences * targetQuantity
		const pricedUnits = this.getPricedUnits().sort((a, b) => a - b).slice(0, discountedUnits)
		if (pricedUnits.length < 1) return null

		let discountAmount = 0
		if (Number.isFinite(percentageValue) && percentageValue > 0) {
			discountAmount = Math.round(
				pricedUnits.reduce((total, price) => total + price, 0) * (percentageValue / 100)
			)
		}
		if (discountAmount <= 0) return null

		return {
			amount: Math.min(charmSubtotal, discountAmount),
			freeCount: percentageValue >= 100 ? discountedUnits : 0,
			title:
				typeof rawDiscount.discount_title === 'string' ? rawDiscount.discount_title.trim() : '',
			source: 'discount-kit-live',
			rewardType:
				typeof rawDiscount.reward_type === 'string' ? rawDiscount.reward_type.trim() : 'PERCENTAGE',
			thresholds: [itemsPerRecurrence],
			rewardValues: [percentageValue],
		}
	}

	private getDiscountAmount(freeCount: number) {
		if (freeCount < 1) return 0

		return this.getPricedUnits()
			.sort((a, b) => a - b)
			.slice(0, freeCount)
			.reduce((total, price) => total + price, 0)
	}

	private getTotalPrice() {
		const selections = this.getSelections()
		const totalSelectedCount = this.getTotalSelectedCount()
		const basePrice = this.getBasePrice()
		const charmSubtotal = selections.reduce(
			(total, selection) => total + selection.price * selection.quantity,
			0
		)
		const discount = this.getActiveDiscount(totalSelectedCount, charmSubtotal).amount

		return basePrice + charmSubtotal - discount
	}

	private getFreeCharmCount(totalSelectedCount: number) {
		const freeCharmEveryCount = Number(this.dataset.freeCharmEveryCount || 0)
		if (!Number.isFinite(freeCharmEveryCount) || freeCharmEveryCount <= 0) return 0

		return Math.floor(totalSelectedCount / freeCharmEveryCount)
	}

	private getFreeCountFromDiscountAmount(discountAmount: number) {
		if (discountAmount <= 0) return 0

		const pricedUnits = this.getPricedUnits().sort((a, b) => a - b)
		let runningDiscount = 0
		let freeCount = 0

		for (const price of pricedUnits) {
			runningDiscount += price
			if (runningDiscount > discountAmount) {
				return 0
			}
			freeCount += 1
			if (runningDiscount === discountAmount) {
				return freeCount
			}
		}

		return 0
	}

	private getPricedUnits() {
		const pricedUnits: number[] = []
		for (const selection of this.getSelections()) {
			for (let index = 0; index < selection.quantity; index += 1) {
				pricedUnits.push(selection.price)
			}
		}
		return pricedUnits
	}

	private getTotalSelectedCount() {
		let count = 0
		for (const selection of this.charmsByIndex.values()) {
			count += selection.quantity
		}
		return count
	}

	private getBasePrice() {
		const selectedSizeInput = this.section?.querySelector<HTMLInputElement>(
			'.size-radios__size-input:checked'
		)
		const selectedPrice = selectedSizeInput?.dataset.price
		if (selectedPrice) return Number(selectedPrice)

		return Number(this.dataset.basePrice || 0)
	}

	private getInventoryStatus() {
		const selectedSizeInput = this.section?.querySelector<HTMLInputElement>(
			'.size-radios__size-input:checked'
		)
		return (
			selectedSizeInput?.dataset.inventoryStatus ||
			this.submitButton?.dataset.inventoryStatus ||
			'InStock'
		)
	}

	private getBaseSubmitLabel() {
		const selectedSizeInput = this.section?.querySelector<HTMLInputElement>(
			'.size-radios__size-input:checked'
		)
		if (selectedSizeInput?.dataset.inventoryStatusText) {
			return selectedSizeInput.dataset.inventoryStatusText
		}

		const activeLabelTarget = this.getActiveSubmitLabelTarget()
		const explicitLabel =
			activeLabelTarget?.querySelector<HTMLElement>('[data-uc-inventory-status-preorder-text], [data-uc-inventory-status-instock-text]')

		return explicitLabel?.textContent?.trim() || 'Add to cart'
	}

	private captureBaseSubmitState(activeLabelTarget: HTMLElement) {
		const textEl =
			activeLabelTarget.querySelector<HTMLElement>('[data-uc-inventory-status-preorder-text]') ||
			activeLabelTarget.querySelector<HTMLElement>('[data-uc-inventory-status-instock-text]')
		const label = textEl?.textContent?.trim() || activeLabelTarget.textContent?.trim() || ''
		if (label && this.submitButton) {
			this.submitButton.dataset.ucCharmsBaseLabel = label
		}
	}

	private getActiveSubmitLabelTarget() {
		if (!this.submitButton) return null

		const preorderEl = this.submitButton.querySelector<HTMLElement>(
			'[data-uc-inventory-status-preorder]:not(.hidden)'
		)
		if (preorderEl) return preorderEl

		const inStockEl = this.submitButton.querySelector<HTMLElement>(
			'[data-uc-inventory-status-instock]:not(.hidden)'
		)
		if (inStockEl) return inStockEl

		return this.submitButton.querySelector<HTMLElement>('[data-uc-inventory-status-text]')
	}

	private decrementSelection(optionIndex: number) {
		const existing = this.charmsByIndex.get(optionIndex)
		if (!existing) return

		if (existing.quantity <= 1) {
			this.charmsByIndex.delete(optionIndex)
			return
		}

		this.charmsByIndex.set(optionIndex, {
			...existing,
			quantity: existing.quantity - 1,
		})
	}

	private animateTile(tile: HTMLElement, stateClass: 'is-adding' | 'is-removing') {
		tile.classList.remove('is-adding', 'is-removing')
		void tile.offsetWidth
		tile.classList.add(stateClass)
		window.setTimeout(() => {
			tile.classList.remove(stateClass)
		}, 280)
	}

	private getBaseSummaryLabel() {
		return this.dataset.baseTitle?.trim() || ''
	}

	private escapeHtml(value: string) {
		return value
			.replaceAll('&', '&amp;')
			.replaceAll('<', '&lt;')
			.replaceAll('>', '&gt;')
			.replaceAll('"', '&quot;')
			.replaceAll("'", '&#39;')
	}

	private parseNumberList(value?: string) {
		if (!value) return []
		try {
			const parsed = JSON.parse(value) as unknown
			if (!Array.isArray(parsed)) return []
			return parsed
				.map((entry) => Number(entry))
				.filter((entry) => Number.isFinite(entry))
		} catch {
			return []
		}
	}

	private renderDebug({
		activeDiscount,
		basePrice,
		charmSubtotal,
		total,
		totalSelectedCount,
	}: {
		activeDiscount: ActiveCharmDiscount
		basePrice: number
		charmSubtotal: number
		total: number
		totalSelectedCount: number
	}) {
		if (!this.debugEl || !this.debugOutputEl) return

		const selections = this.getSelections().map((selection) => ({
			index: selection.index,
			id: selection.id,
			title: selection.title,
			price_cents: selection.price,
			quantity: selection.quantity,
			line_subtotal_cents: selection.price * selection.quantity,
		}))

			const debugPayload = {
				source: activeDiscount.source,
				base_price_cents: basePrice,
				charm_subtotal_cents: charmSubtotal,
				discount_amount_cents: activeDiscount.amount,
				total_cents: total,
				selected_charm_count: totalSelectedCount,
				free_count: activeDiscount.freeCount,
				theme_setting_free_charm_every_count: Number(this.dataset.freeCharmEveryCount || 0),
				discount_title: activeDiscount.title,
				reward_type: activeDiscount.rewardType,
				thresholds: activeDiscount.thresholds,
				reward_values: activeDiscount.rewardValues,
				raw_discount_kit: this.parseJsonData(this.dataset.discountKitRaw),
				visible_discount_kit_discounts: this.parseJsonData(
					this.dataset.discountKitVisibleDiscounts
				),
				shop_discount_kit_discounts: this.parseJsonData(this.dataset.discountKitShopDiscounts),
				selections,
			}

		this.debugOutputEl.textContent = JSON.stringify(debugPayload, null, 2)
	}

	private parseJsonData(value?: string) {
		if (!value) return null
		try {
			return JSON.parse(value) as unknown
		} catch {
			return value
		}
	}

	private isRecord(value: unknown): value is Record<string, unknown> {
		return typeof value === 'object' && value !== null && !Array.isArray(value)
	}

	private template(template: string, count: number, price = '') {
		return template
			.replace('{{ count }}', String(count))
			.replace('{{ price }}', price)
	}

	private setElementVisible(element: HTMLElement, isVisible: boolean) {
		element.toggleAttribute('hidden', !isVisible)
		element.classList.toggle('hidden', !isVisible)
	}
}
